import React, { useEffect, useState } from 'react';
import { CommonPopover } from '../common-popover/common-popover';
import { CommonPopoverContext } from '../common-popover/common-popover.context';
import { SearchInput } from '../search-input/search-input';
import { Component } from '../../../infrastructure/type-extensions';
import './make-multi-select.scss';
import { DefaultClosePopoverButton } from '../common-popover/default-close-popover-button';
import { getFilteredAttendees } from '../../../infrastructure/utils/attendee.utils';
import KnovioPreviewSpinner from '../spinner/knovio-preview-spinner';
import { FormattedMessage } from 'react-intl';
import { LOADING } from '../../../constants/common-labels.constants';
import { MultiSelectOrganizationList } from '../multi-select-list/multi-select-organization-list';

interface IMultiSelectClosedPropsDerivedFromItemsList {
  isOpen: boolean;
}

const makeOrganizationMultiSelect = <T extends IMultiSelectClosedPropsDerivedFromItemsList>(ComponentWhichTriggerSingleSelection: Component<T>) => {
  const ComponentWithForwardedRef = React.forwardRef<HTMLElement, T>((props: T, ref: React.Ref<HTMLElement>) => {
    return (
      <CommonPopoverContext.Consumer>
        {context => <ComponentWhichTriggerSingleSelection {...props} isOpen={context.isOpen} ref={ref} />}
      </CommonPopoverContext.Consumer>
    );
  });

  return props => {
    const {
        value,
        items,
        applyButtonText,
        resetButtonText,
        searchPlaceholder,
        ActionButton,
        emptyListText,
        emptyFilterText,
        disabled,
        isWide,
        onChange,
        position,
        align,
        isWalkIn,
        organizationFollowsAttendees,
        loadingAttendees,
        resetLoadingState,
        applyButtonDisabled = false
    } = props;
    const [filterCriteria, setFilterCriteria] = useState<string>('');
    const [selectedValues, setSelectedValues] = useState(value);
    const [loading, setLoading] = useState(loadingAttendees);

    let filteredItems = [];

    if (items) {
        if (!filterCriteria) {
        filteredItems = items;
        } else {
        filteredItems = getFilteredAttendees(items, filterCriteria);
        }
    }

    const shallRenderLoader = loading || !items;
    let shallRenderNoItems = false;

    if (!shallRenderLoader) {
        shallRenderNoItems = Array.isArray(items)
            ? items.length === 0
            : Object.keys(items).length === 0;
    }

    const shallRenderEmptySearch = !shallRenderLoader && !shallRenderNoItems && Object.keys(filteredItems).length === 0;
    const shallRenderMultiSelect = !shallRenderLoader && !shallRenderNoItems && !shallRenderEmptySearch;

    const handleClose = () => {
      setSelectedValues(value);
      setFilterCriteria('');
      resetLoadingState?.();
    };

    const AddNewEntityButton = (
      <CommonPopoverContext.Consumer>
          {context => (
              <>
                { !!ActionButton && <div onClick={context.closePopover}><ActionButton /></div> }
              </>
          )}
      </CommonPopoverContext.Consumer>
  );

  useEffect(() => {
      setLoading(loadingAttendees);
  }, [loadingAttendees]);

    return (
      <CommonPopover
        header={<SearchInput
          placeholder={searchPlaceholder || 'Search'}
          onChange={e => setFilterCriteria(e.target.value)}
          className='w-100'
          value={filterCriteria}
          autoFocus= {true}
        />}
        body={(
          <>
            {AddNewEntityButton}
            { shallRenderLoader && (
                <div className='single-select-no-content organizations-multi-select-loader w-100'>
                    <KnovioPreviewSpinner />
                    <FormattedMessage
                        tagName='span'
                        {...LOADING}
                        values={{
                            suffix: '...'
                        }}
                    />
                </div>
            )}
            { shallRenderNoItems && <div className='single-select-no-content'>{emptyListText || 'No items'}</div>}
            { shallRenderEmptySearch &&
              <div className='single-select-no-content'>{emptyFilterText || 'No items matching search criteria'}</div>}
            { shallRenderMultiSelect && (
              <MultiSelectOrganizationList
                values={selectedValues}
                disabledItems={disabled}
                items={filteredItems}
                onChange={checkedOptions => setSelectedValues(checkedOptions)}
                isWide={isWide}
                isWalkIn={isWalkIn}
                organizationFollowsAttendees={organizationFollowsAttendees}
              />
            )}
          </>
        )}
        footer={
          <div className='buttons-block buttons-block--from-right'>
            <DefaultClosePopoverButton
              text={applyButtonText}
              modifier={['fat', 'confirm-safe']}
              onClick={() => onChange(selectedValues)}
              disabled={applyButtonDisabled}
            />
            <DefaultClosePopoverButton text={resetButtonText} />
          </div>
        }
        position={position || 'left'}
        align={align || 'start'}
        onClose={handleClose}
      >
        <ComponentWithForwardedRef {...props} />
      </CommonPopover>
    );
  };
};

export { makeOrganizationMultiSelect, IMultiSelectClosedPropsDerivedFromItemsList };
